import { memo } from 'react';

import { CodeEditor } from '@/components/monaco/codeEditor/codeEditor';
import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import type { WidgetData } from '@/components/ui/widget/widget.type';
import { useWidgetRenderContext } from '@/components/ui/widget/widgetRenderContext';
import { TestId } from '@/types/testId';
import { getByPath } from '@/utils/widgets';
import type { CodeView } from './code.type';

type Props = {
  view: CodeView;
  data: WidgetData;
};

// display a code snippet in Monaco editor
const CodeViewImpl = memo(({ view, data }: Props) => {
  const { path, monacoOptions } = view;
  const renderContext = useWidgetRenderContext();
  const isTableCell = renderContext === 'table-cell';
  const codeEditorOptions = isTableCell
    ? {
        ...monacoOptions,
        lineNumbers: 'off' as const
      }
    : monacoOptions;

  const codeData = getByPath(data, path);
  if (codeData === undefined) return null;

  return (
    <div
      className={isTableCell ? 'h-full min-h-0 w-full' : 'h-50'}
      data-testid={TestId.Widget.Code.CodeView}
    >
      <CodeEditor
        height="100%"
        minHeight={isTableCell ? 0 : '50px'}
        options={codeEditorOptions}
        readOnly={true}
        value={codeData}
      />
    </div>
  );
});

// sort defaults to text on path
const getSortConfig = (view: CodeView) => ({
  type: 'text',
  path: view.path
});

// filtering defaults to text search on path
const getFilterConfig = (view: CodeView) => ({
  type: 'text',
  path: view.path
});

// width defaults to 'xlarge'
const getDefaultWidth = (): ColumnWidth => 'xlarge';

// stringifies data at path
const getStringifyFunction = (view: CodeView) => {
  const { path } = view;

  return (data: WidgetData) => {
    const codeData = getByPath(data, path);
    if (codeData === undefined) return '';

    return JSON.stringify(codeData, null, 2);
  };
};

export const codeViewProvider = {
  component: CodeViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
